import { gql } from "@apollo/client";
import {
  SemanticProjectFileId,
  SemanticProjectId,
  SemanticProjectVersionId,
  asciiCompare,
  typedObjectFromEntries,
} from "@hex/common";
import {
  EntityState,
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { memoize } from "lodash";
import { createSelector } from "../utils/index.js";

import { RootState } from "../store";
import {
  assertNonNull,
  getSelectorsForNullableEntityState,
} from "../utils/index.js";

import { SemanticProjectVersionMpInitFragment } from "./semanticProjectVersionMPSlice.generated";

// *** State *****************************************************************
gql`
  fragment SemanticProjectVersionMPInit on SemanticProjectVersion {
    id
    revision
    files {
      id
      revision
      filePath
      contentsText
    }
    semanticProject {
      id
    }
  }
`;

export type SemanticProjectVersionMP = Omit<
  SemanticProjectVersionMpInitFragment,
  "files" | "semanticProject"
> & {
  // replace `semanticDatasets` with an entity adapter so that we get fast
  // access and consistent ordering when multiple updates occur
  files: EntityState<SemanticProjectFileMP>;
  // lift the id up a layer to simplify access
  semanticProjectId: SemanticProjectId;
};
const semanticProjectVersionAdapter =
  createEntityAdapter<SemanticProjectVersionMP>({
    selectId: (version) => version.id,
    sortComparer: (a, b) => asciiCompare(a.id, b.id),
  });
const semanticFilesAdapter = createEntityAdapter<SemanticProjectFileMP>({
  selectId: (file) => file.id,
  sortComparer: (a, b) => asciiCompare(a.filePath, b.filePath),
});

export type SemanticProjectFileMP =
  SemanticProjectVersionMpInitFragment["files"][0];

type SemanticProjectVersionMPSliceState = EntityState<SemanticProjectVersionMP>;

const initialState: SemanticProjectVersionMPSliceState =
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- this is okay
  semanticProjectVersionAdapter.getInitialState();

// *** Actions ****************************************************************
type SemanticProjectVersionAction<D = object> = PayloadAction<
  D & { semanticProjectVersionId: SemanticProjectVersionId }
>;
type SemanticFileAction<D = object> = SemanticProjectVersionAction<
  D & { semanticFileId: SemanticProjectFileId }
>;

const semanticProjectVersionMPSlice = createSlice({
  name: "semanticProjectVersionMP",
  initialState,
  reducers: {
    /** Load a new semantic project version into the store for use. */
    initialize(
      state,
      action: PayloadAction<SemanticProjectVersionMpInitFragment>,
    ) {
      const version: SemanticProjectVersionMP = {
        ...action.payload,
        semanticProjectId: action.payload.semanticProject.id,
        files: semanticFilesAdapter.getInitialState(),
      };
      version.files = semanticFilesAdapter.setAll(
        version.files,
        action.payload.files,
      );
      return semanticProjectVersionAdapter.setOne(state, version);
    },

    /** Updates the specified file's contents in the semantic project version. */
    upsertFile(
      state,
      action: SemanticProjectVersionAction<{
        file: SemanticProjectFileMP;
      }>,
    ) {
      const version = selectSemanticProjectVersion(
        state,
        action.payload.semanticProjectVersionId,
      );
      version.files = semanticFilesAdapter.upsertOne(
        version.files,
        action.payload.file,
      );
    },

    /** Removes the specified file from the semantic project version. */
    removeFile(state, action: SemanticFileAction) {
      const version = selectSemanticProjectVersion(
        state,
        action.payload.semanticProjectVersionId,
      );
      version.files = semanticFilesAdapter.removeOne(
        version.files,
        action.payload.semanticFileId,
      );
    },
  },
});

export const semanticProjectVersionMPReducer =
  semanticProjectVersionMPSlice.reducer;

export const semanticProjectVersionMPActions =
  semanticProjectVersionMPSlice.actions;

// *** Selectors **************************************************************
function selectSlice(state: RootState): SemanticProjectVersionMPSliceState {
  return state.semanticProjectVersionMP;
}
function selectSemanticProjectVersion(
  sliceState: SemanticProjectVersionMPSliceState,
  semanticProjectVersionId: SemanticProjectVersionId,
  safe: true,
): SemanticProjectVersionMP | null;
function selectSemanticProjectVersion(
  sliceState: SemanticProjectVersionMPSliceState,
  semanticProjectVersionId: SemanticProjectVersionId,
  safe?: false,
): SemanticProjectVersionMP;
function selectSemanticProjectVersion(
  sliceState: SemanticProjectVersionMPSliceState,
  semanticProjectVersionId: SemanticProjectVersionId,
  safe: boolean = false,
): SemanticProjectVersionMP | null {
  const version = sliceState.entities[semanticProjectVersionId];
  if (!safe) {
    assertNonNull(
      version,
      `No semantic project version initialized with id ${semanticProjectVersionId}`,
    );
  }
  return version!;
}

export const semanticProjectVersionMPSelectors = {
  projectVersion: memoize(
    (semanticProjectVersionId: SemanticProjectVersionId) => ({
      select: (state: RootState): SemanticProjectVersionMP =>
        selectSemanticProjectVersion(
          selectSlice(state),
          semanticProjectVersionId,
        ),
      safeSelect: (state: RootState): SemanticProjectVersionMP | null =>
        selectSemanticProjectVersion(
          selectSlice(state),
          semanticProjectVersionId,
          true,
        ),
    }),
  ),
  file: memoize((semanticProjectVersionId: SemanticProjectVersionId) => ({
    ...semanticFilesAdapter.getSelectors(
      (s: RootState) =>
        selectSemanticProjectVersion(selectSlice(s), semanticProjectVersionId)
          .files,
    ),
    safe: getSelectorsForNullableEntityState(
      semanticFilesAdapter,
      (s: RootState) =>
        selectSemanticProjectVersion(
          selectSlice(s),
          semanticProjectVersionId,
          true,
        )?.files,
    ),
    selectAllFilePaths: createSelector(
      [
        (s: RootState) =>
          selectSemanticProjectVersion(
            selectSlice(s),
            semanticProjectVersionId,
            true,
          )?.files,
      ],
      (files) =>
        new Set(
          files?.ids
            .map((id) => files.entities[id]?.filePath)
            .filter((f) => f != null) ?? [],
        ),
    ),
  })),
};

/**
 * @example
 * const fileId = useSelector((s) => selectFilesByFilePath(s, semanticProjectVersionId)[filePath]?.id);
 */
export const selectFilesByFilePath = createSelector(
  [
    (s: RootState, semanticProjectVersionId: SemanticProjectVersionId) =>
      semanticProjectVersionMPSelectors
        .file(semanticProjectVersionId)
        .selectAll(s),
  ],
  (files) => typedObjectFromEntries(files.map((d) => [d.filePath, d])),
);
